import React from "react";
import { toast } from "react-toastify";
import DropLobbyPage from "../pages/DropLobbyPage";
import {
  CreateSimLobbyRequest,
  SimLobbyDetail,
  SimLobbyStepAction,
  useCreateSimLobbyMutation,
  useResetSimLobbyMutation,
  useStepSimLobbyMutation,
} from "../api/respectCardsApi";
import { getApiErrorMessage } from "../lib/utils";

/**
 * cards-drop-lobby phase 12 — dev sim control panel.
 *
 * Drives the phase-11 sim-lobby endpoints (create / step / reset) and renders
 * the REAL phase-8 lobby UI ({@link DropLobbyPage} in `simLobby` mode) against
 * the returned snapshot — so the whole gamified flow (gathering → odds → timed
 * reveal → done, plus late joiners, blocks, and the 4.5s timing curve) is
 * watchable end-to-end without real users or real time.
 *
 * Admin-only + dev-only: the parent (`CardsAdminPage`) shows this tab only when
 * NODE_ENV !== 'production', and the backend routes 404 in production anyway.
 */

const box: React.CSSProperties = {
  background: "#fff",
  padding: 16,
  borderRadius: 8,
  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
  marginBottom: 16,
};

const sectionTitle: React.CSSProperties = {
  fontSize: "1.1rem",
  color: "#333",
  marginBottom: 12,
};

const subTitle: React.CSSProperties = {
  fontSize: "0.92rem",
  color: "#333",
  margin: "0 0 8px",
  fontWeight: 700,
};

const help: React.CSSProperties = {
  fontSize: "0.82rem",
  color: "#666",
  lineHeight: 1.5,
  margin: "0 0 12px",
};

const btn: React.CSSProperties = {
  fontSize: "0.82rem",
  padding: "6px 12px",
  borderRadius: 6,
  border: "1px solid #c7c1e0",
  background: "#fff",
  color: "#3730a3",
  cursor: "pointer",
};

const btnPrimary: React.CSSProperties = {
  ...btn,
  background: "#4f46e5",
  borderColor: "#4f46e5",
  color: "#fff",
};

const btnDanger: React.CSSProperties = {
  ...btn,
  background: "#ffebee",
  borderColor: "#c62828",
  color: "#c62828",
};

const inputCss: React.CSSProperties = {
  fontSize: "0.85rem",
  padding: "6px 10px",
  borderRadius: 6,
  border: "1px solid #d1d5db",
};

const fieldLabel: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
  fontSize: "0.76rem",
  color: "#555",
  minWidth: 120,
};

const tile: React.CSSProperties = {
  border: "1px solid #ece6f7",
  borderRadius: 8,
  background: "#faf7ff",
  padding: 12,
};

const kicker: React.CSSProperties = {
  fontSize: "0.72rem",
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "#6d5a9a",
  marginBottom: 6,
};

const mono: React.CSSProperties = {
  fontFamily: "monospace",
  fontSize: "0.78rem",
  color: "#4b3b72",
  wordBreak: "break-word",
};

const PARTICIPANTS_PLACEHOLDER = `[
  { "username": "ace", "modifiers": { "coffeeRatingBonus": 0.08, "creatorBonus": 0.12 } },
  { "username": "bo", "modifiers": { "droughtBonus": 0.25 } },
  { "username": "cy" }
]`;

const SLOTS_PLACEHOLDER = `[
  { "rarity": "legendary", "baseHitChance": 0.05 },
  { "rarity": "epic", "baseHitChance": 0.15 },
  { "rarity": "rare", "baseHitChance": 0.4 }
]`;

function fmtPct(x: number | null | undefined): string {
  if (x == null) return "—";
  return `${Math.round(x * 1000) / 10}%`;
}

function fmtMult(x: number | null | undefined): string {
  if (x == null) return "—";
  return `×${Math.round(x * 100) / 100}`;
}

const DropLobbySimPanel: React.FC = () => {
  const [detail, setDetail] = React.useState<SimLobbyDetail | null>(null);
  const [viewerId, setViewerId] = React.useState<string | null>(null);

  // create form
  const [numUsers, setNumUsers] = React.useState("3");
  const [gatheringWindowMs, setGatheringWindowMs] = React.useState("10000");
  const [turnWindowMs, setTurnWindowMs] = React.useState("10000");
  const [participantsJson, setParticipantsJson] = React.useState("");
  const [slotsJson, setSlotsJson] = React.useState("");
  const [showAdvanced, setShowAdvanced] = React.useState(false);

  // step form
  const [elapsedMs, setElapsedMs] = React.useState(4500);
  const [forcedMultiplier, setForcedMultiplier] = React.useState("");
  const [pullTarget, setPullTarget] = React.useState("");
  const [lateUsername, setLateUsername] = React.useState("");
  const [blockUsername, setBlockUsername] = React.useState("");

  const [createSimLobby, createState] = useCreateSimLobbyMutation();
  const [stepSimLobby, stepState] = useStepSimLobbyMutation();
  const [resetSimLobby, resetState] = useResetSimLobbyMutation();

  const snapshot = detail?.snapshot ?? null;
  const turnWindow = snapshot?.turnWindowMs ?? 10000;

  // Keep the viewer selection valid as participants change.
  React.useEffect(() => {
    if (!snapshot) {
      setViewerId(null);
      return;
    }
    const ids = snapshot.participants.map((p) => p.userId);
    setViewerId((prev) => {
      if (prev && ids.includes(prev)) return prev;
      return snapshot.currentTurn?.userId ?? ids[0] ?? null;
    });
  }, [snapshot]);

  function applyDetail(next: SimLobbyDetail): void {
    setDetail(next);
  }

  async function handleCreate(): Promise<void> {
    const body: CreateSimLobbyRequest = {};
    const n = Number.parseInt(numUsers, 10);
    if (Number.isFinite(n) && n > 0) body.numUsers = n;
    const gw = Number.parseInt(gatheringWindowMs, 10);
    if (Number.isFinite(gw) && gw > 0) body.gatheringWindowMs = gw;
    const tw = Number.parseInt(turnWindowMs, 10);
    if (Number.isFinite(tw) && tw > 0) body.turnWindowMs = tw;

    if (participantsJson.trim()) {
      try {
        const parsed = JSON.parse(participantsJson);
        if (!Array.isArray(parsed)) throw new Error("participants must be an array");
        body.participants = parsed;
      } catch (e) {
        toast.error(`participants JSON: ${(e as Error).message}`, { position: "bottom-right" });
        return;
      }
    }
    if (slotsJson.trim()) {
      try {
        const parsed = JSON.parse(slotsJson);
        if (!Array.isArray(parsed)) throw new Error("slots must be an array");
        body.slots = parsed;
      } catch (e) {
        toast.error(`slots JSON: ${(e as Error).message}`, { position: "bottom-right" });
        return;
      }
    }

    try {
      const res = await createSimLobby(body).unwrap();
      applyDetail(res);
      toast.success(`sim lobby created (${res.sim.participants.length} fakes)`, {
        position: "bottom-right",
      });
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to create sim lobby."), {
        position: "bottom-right",
      });
    }
  }

  async function runStep(
    action: SimLobbyStepAction,
    extra: { elapsedMs?: number; timingMultiplier?: number; username?: string } = {},
  ): Promise<void> {
    if (!detail) return;
    try {
      const res = await stepSimLobby({ id: detail.simLobbyId, action, ...extra }).unwrap();
      applyDetail(res);
      if (action === "pull" && res.pullResult) {
        const r = res.pullResult;
        toast.success(
          `pull: ${r.outcome ?? "—"}${r.jackpot ? " · JACKPOT" : ""} · ${fmtMult(
            r.timingMultiplier,
          )} ${r.timingZone ?? ""} · eff ${fmtPct(r.effectiveChance)}`,
          { position: "bottom-right" },
        );
      }
    } catch (err) {
      const anyErr = err as { data?: { message?: string } };
      toast.error(anyErr?.data?.message || getApiErrorMessage(err, `Step "${action}" failed.`), {
        position: "bottom-right",
      });
    }
  }

  function handlePullCurrent(): void {
    const forced = forcedMultiplier.trim() ? Number.parseFloat(forcedMultiplier) : NaN;
    if (forcedMultiplier.trim() && Number.isFinite(forced)) {
      void runStep("pull", {
        timingMultiplier: forced,
        ...(pullTarget.trim() ? { username: pullTarget.trim() } : {}),
      });
      return;
    }
    void runStep("pull", {
      elapsedMs,
      ...(pullTarget.trim() ? { username: pullTarget.trim() } : {}),
    });
  }

  async function handleReset(): Promise<void> {
    if (!detail) return;
    try {
      await resetSimLobby(detail.simLobbyId).unwrap();
      setDetail(null);
      toast.info("sim lobby torn down.", { position: "bottom-right" });
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to reset sim lobby."), {
        position: "bottom-right",
      });
    }
  }

  const status = snapshot?.status ?? null;
  const stepping = stepState.isLoading;

  return (
    <div>
      <section style={box}>
        <h2 style={sectionTitle}>🧪 Sim lobby</h2>
        <p style={help}>
          dev-only. creates a REAL <code>CardDropLobby</code> flagged{" "}
          <code>simulated:true</code> on a far-future date and drives the same
          engine the live drop uses. the lobby UI below renders the actual phase-8
          components against the sim snapshot — step it from the controls and watch
          it update. sim pulls never touch a real hand or the public activity feed.
        </p>

        {!detail ? (
          <div>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
              <label style={fieldLabel}>
                participants
                <input
                  type="number"
                  min={1}
                  max={50}
                  value={numUsers}
                  onChange={(e) => setNumUsers(e.target.value)}
                  style={inputCss}
                />
              </label>
              <label style={fieldLabel}>
                gathering window (ms)
                <input
                  type="number"
                  value={gatheringWindowMs}
                  onChange={(e) => setGatheringWindowMs(e.target.value)}
                  style={inputCss}
                />
              </label>
              <label style={fieldLabel}>
                turn window (ms)
                <input
                  type="number"
                  value={turnWindowMs}
                  onChange={(e) => setTurnWindowMs(e.target.value)}
                  style={inputCss}
                />
              </label>
            </div>

            <button
              type="button"
              style={{ ...btn, marginBottom: 12 }}
              onClick={() => setShowAdvanced((v) => !v)}
            >
              {showAdvanced ? "hide advanced" : "advanced: per-user modifiers + slots"}
            </button>

            {showAdvanced ? (
              <div style={{ display: "grid", gap: 12, marginBottom: 12 }}>
                <label style={{ ...fieldLabel, minWidth: 0 }}>
                  participants JSON (overrides count — modifier presets per user)
                  <textarea
                    value={participantsJson}
                    onChange={(e) => setParticipantsJson(e.target.value)}
                    placeholder={PARTICIPANTS_PLACEHOLDER}
                    rows={5}
                    style={{ ...inputCss, fontFamily: "monospace", resize: "vertical" }}
                  />
                </label>
                <label style={{ ...fieldLabel, minWidth: 0 }}>
                  slots JSON (slot composition — rarity + baseHitChance)
                  <textarea
                    value={slotsJson}
                    onChange={(e) => setSlotsJson(e.target.value)}
                    placeholder={SLOTS_PLACEHOLDER}
                    rows={5}
                    style={{ ...inputCss, fontFamily: "monospace", resize: "vertical" }}
                  />
                </label>
              </div>
            ) : null}

            <button
              type="button"
              style={btnPrimary}
              onClick={() => void handleCreate()}
              disabled={createState.isLoading}
            >
              {createState.isLoading ? "creating…" : "Create sim lobby"}
            </button>
          </div>
        ) : (
          <div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                gap: 12,
                marginBottom: 16,
              }}
            >
              <div style={tile}>
                <div style={kicker}>Sim lobby</div>
                <div style={mono}>{detail.simLobbyId}</div>
                <div style={{ fontSize: "0.78rem", color: "#555" }}>date: {detail.date}</div>
              </div>
              <div style={tile}>
                <div style={kicker}>Status</div>
                <div style={{ fontSize: "1rem", fontWeight: 700, color: "#2e2550" }}>
                  {status ?? "—"}
                </div>
                <div style={{ fontSize: "0.78rem", color: "#555" }}>
                  reveal #{snapshot?.revealIndex ?? 0} · {snapshot?.participantCount ?? 0} in
                </div>
              </div>
              <div style={tile}>
                <div style={kicker}>Seed</div>
                <div style={mono}>{detail.sim.seed ?? "—"}</div>
                <div style={{ fontSize: "0.78rem", color: "#555" }}>
                  {detail.sim.slots.length} slots
                </div>
              </div>
            </div>

            {/* ── step controls ─────────────────────────────────────────── */}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
              <button
                type="button"
                style={btnPrimary}
                onClick={() => void runStep("close-gathering")}
                disabled={stepping || status !== "gathering"}
              >
                Close gathering
              </button>
              <button
                type="button"
                style={btn}
                onClick={() => void runStep("auto-pull")}
                disabled={stepping || status !== "revealing"}
              >
                Auto-pull (expire turn)
              </button>
              <button type="button" style={btnDanger} onClick={() => void handleReset()} disabled={resetState.isLoading}>
                {resetState.isLoading ? "resetting…" : "Reset / tear down"}
              </button>
            </div>

            {/* ── pull with timing ──────────────────────────────────────── */}
            <div style={{ ...tile, marginBottom: 14 }}>
              <p style={subTitle}>Pull (timing curve)</p>
              <p style={{ ...help, margin: "0 0 8px" }}>
                slide to choose when within the turn the pull lands. target is{" "}
                <strong>4.5s</strong> (the sweet spot). a forced multiplier bypasses the
                elapsed→multiplier curve; ≥ the jackpot multiplier forces a jackpot.
              </p>
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <input
                  type="range"
                  min={0}
                  max={turnWindow}
                  step={10}
                  value={Math.min(elapsedMs, turnWindow)}
                  onChange={(e) => setElapsedMs(Number(e.target.value))}
                  style={{ flex: 1, minWidth: 200 }}
                  aria-label="elapsed ms within the turn"
                />
                <span style={{ ...mono, minWidth: 92 }}>
                  {(elapsedMs / 1000).toFixed(2)}s ({elapsedMs}ms)
                </span>
                <button type="button" style={btn} onClick={() => setElapsedMs(4500)}>
                  → 4.5s
                </button>
              </div>
              <div
                style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap", marginTop: 10 }}
              >
                <label style={fieldLabel}>
                  force multiplier (optional)
                  <input
                    type="number"
                    step="0.1"
                    value={forcedMultiplier}
                    onChange={(e) => setForcedMultiplier(e.target.value)}
                    placeholder="e.g. 3 or 100"
                    style={inputCss}
                  />
                </label>
                <label style={fieldLabel}>
                  target username (optional — late joiner)
                  <input
                    type="text"
                    value={pullTarget}
                    onChange={(e) => setPullTarget(e.target.value)}
                    placeholder="defaults to current turn"
                    style={inputCss}
                  />
                </label>
                <button
                  type="button"
                  style={btnPrimary}
                  onClick={handlePullCurrent}
                  disabled={stepping || status === "gathering"}
                >
                  {stepping ? "pulling…" : "Pull"}
                </button>
              </div>
            </div>

            {/* ── late joiner + block ───────────────────────────────────── */}
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 16 }}>
              <div style={{ ...tile, flex: 1, minWidth: 220 }}>
                <p style={subTitle}>Add late joiner</p>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <input
                    type="text"
                    value={lateUsername}
                    onChange={(e) => setLateUsername(e.target.value)}
                    placeholder="username (optional)"
                    style={{ ...inputCss, flex: 1, minWidth: 120 }}
                  />
                  <button
                    type="button"
                    style={btn}
                    onClick={() =>
                      void runStep("add-late-joiner", {
                        ...(lateUsername.trim() ? { username: lateUsername.trim() } : {}),
                      }).then(() => setLateUsername(""))
                    }
                    disabled={stepping}
                  >
                    Add
                  </button>
                </div>
              </div>
              <div style={{ ...tile, flex: 1, minWidth: 220 }}>
                <p style={subTitle}>Block / unblock</p>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <input
                    type="text"
                    value={blockUsername}
                    onChange={(e) => setBlockUsername(e.target.value)}
                    placeholder="username"
                    style={{ ...inputCss, flex: 1, minWidth: 120 }}
                  />
                  <button
                    type="button"
                    style={btnDanger}
                    onClick={() => void runStep("block", { username: blockUsername.trim() })}
                    disabled={stepping || !blockUsername.trim()}
                  >
                    Block
                  </button>
                  <button
                    type="button"
                    style={btn}
                    onClick={() => void runStep("unblock", { username: blockUsername.trim() })}
                    disabled={stepping || !blockUsername.trim()}
                  >
                    Unblock
                  </button>
                </div>
              </div>
            </div>

            {/* ── viewer selector ───────────────────────────────────────── */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: "0.82rem", color: "#555" }}>view as:</span>
              <select
                value={viewerId ?? ""}
                onChange={(e) => setViewerId(e.target.value || null)}
                style={inputCss}
              >
                {snapshot?.participants.map((p) => (
                  <option key={p.userId} value={p.userId}>
                    {p.username} (#{p.arrivalRank + 1})
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </section>

      {/* ── the REAL lobby UI, rendered against sim data ─────────────────── */}
      {detail && snapshot ? (
        <section style={{ ...box, padding: 0, overflow: "hidden" }}>
          <DropLobbyPage simLobby={snapshot} simViewerId={viewerId} />
        </section>
      ) : null}

      {/* ── sim-only debug internals ─────────────────────────────────────── */}
      {detail ? (
        <section style={box}>
          <p style={subTitle}>Debug internals (sim-only)</p>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.8rem" }}>
              <thead>
                <tr style={{ textAlign: "left", color: "#666" }}>
                  <th style={{ padding: 6 }}>participant</th>
                  <th style={{ padding: 6 }}>status</th>
                  <th style={{ padding: 6 }}>modifiers</th>
                  <th style={{ padding: 6 }}>odds (pre → eff)</th>
                  <th style={{ padding: 6 }}>pull result</th>
                </tr>
              </thead>
              <tbody>
                {detail.sim.participants.map((p) => (
                  <tr key={p.userId} style={{ borderTop: "1px solid #eee" }}>
                    <td style={{ padding: 6 }}>
                      <strong>{p.username}</strong> #{p.arrivalRank + 1}
                      {p.isLateJoiner ? " · late" : ""}
                    </td>
                    <td style={{ padding: 6 }}>{p.pullStatus}</td>
                    <td style={{ padding: 6, ...mono }}>
                      a{fmtPct(p.modifiers.arrivalBonus)} · ☕{fmtPct(p.modifiers.coffeeRatingBonus)} · 🎨
                      {fmtPct(p.modifiers.creatorBonus)} · 🌵{fmtPct(p.modifiers.droughtBonus)}
                    </td>
                    <td style={{ padding: 6, ...mono }}>
                      {p.slotChances.map((s) => (
                        <div key={s.slotIndex}>
                          {s.rarity}: {fmtPct(s.preTimingChance)} → {fmtPct(s.effectiveChance)}
                        </div>
                      ))}
                    </td>
                    <td style={{ padding: 6, ...mono }}>
                      {p.pullResult ? (
                        <>
                          {p.pullResult.outcome ?? "—"}
                          {p.pullResult.jackpot ? " · JACKPOT" : ""}
                          <div>
                            {fmtMult(p.pullResult.timingMultiplier)} {p.pullResult.timingZone ?? ""}
                          </div>
                          <div>
                            eff {fmtPct(p.pullResult.effectiveChance)}
                            {p.pullResult.elapsedMs != null
                              ? ` · ${(p.pullResult.elapsedMs / 1000).toFixed(2)}s`
                              : ""}
                          </div>
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </div>
  );
};

export default DropLobbySimPanel;
